import { useEffect, useId, useRef, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import { Button } from './Button';
import { Icon } from './Icon';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  /** Lado de onde a gaveta sai. Por padrao, a direita, como o cartao da esteira. */
  side?: 'right' | 'left' | undefined;
  size?: 'md' | 'lg' | 'xl' | undefined;
}

const larguras = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
} as const;

/**
 * Gaveta lateral, como a do cartao da esteira do OnPoint: abre por cima da lista sem
 * tirar a pessoa dela.
 *
 * Usa o mesmo elemento nativo de dialogo do Modal, pelo mesmo motivo: retencao de
 * foco, fechamento por escape e camada de fundo vem do navegador. O que muda e a
 * posicao, colada na lateral e na altura inteira da tela, com o conteudo rolando por
 * dentro e o rodape fixo.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  side = 'right',
  size = 'lg',
}: DrawerProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const tituloId = useId();
  const descricaoId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      aria-labelledby={tituloId}
      aria-describedby={description ? descricaoId : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Clique na camada de fundo fecha; dentro do conteudo, nao.
        if (event.target === ref.current) onClose();
      }}
      className={cn(
        'fixed inset-y-0 m-0 h-dvh max-h-dvh w-full border-line bg-surface p-0',
        'shadow-(--shadow-lg) backdrop:bg-ink-900/40',
        'open:animate-[fade-in_var(--duration-base)_ease-out]',
        side === 'right' ? 'right-0 left-auto border-l' : 'left-0 right-auto border-r',
        larguras[size],
      )}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={tituloId} className="truncate text-sm font-semibold text-ink-900">
              {title}
            </h2>
            {description ? (
              <p id={descricaoId} className="mt-0.5 text-xs text-ink-500">
                {description}
              </p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Fechar"
            className="-mr-2 -mt-1"
          >
            <Icon name="close" size={16} />
          </Button>
        </div>

        <div className="rolagem-fina min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        {footer ? (
          <div className="flex justify-end gap-2 border-t border-line bg-surface-sunken px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </dialog>
  );
}
